import { useEffect, useState } from 'react';
import { IonSpinner } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import type { CancelPreviewResponse } from '../../types/order';
import { formatPriceCents } from '../../utils/currency';
import { Modal } from '../ui/Modal';
import { RadioOptionList } from '../ui/RadioOptionList';

// `labelEn` is what we ship to the BE in `rejectionReason` — keeping it in
// English preserves analytics/log readability regardless of which lang the
// user picked. The displayed text comes from orders.json via t() at render
// time. The `key` matches the orders.json sub-key so the lookup is direct.
const LISTER_REJECTION_REASONS = [
  { key: 'less_portions', labelEn: "I have fewer portions than I thought" },
  { key: 'know_person', labelEn: "I know this person and prefer not to fulfill" },
  { key: 'dont_know_person', labelEn: "I don't know this person and prefer not to fulfill" },
  { key: 'pickup_time', labelEn: 'Pick up time did not work for me' },
  { key: 'other', labelEn: 'Other' },
] as const;

// Same list for a drop-off order, with the time reason naming the right window.
const LISTER_DROPOFF_REJECTION_REASONS = LISTER_REJECTION_REASONS.map((r) =>
  r.key === 'pickup_time'
    ? { key: 'dropoff_time', labelEn: 'Drop-off time did not work for me' }
    : r,
);

const EATER_CANCEL_REASONS = [
  { key: 'changed_mind', labelEn: 'Changed my mind' },
  { key: 'different_option', labelEn: 'Found a different option' },
  { key: 'schedule', labelEn: 'Schedule no longer works' },
  { key: 'mistake', labelEn: 'Ordered by mistake' },
  { key: 'other', labelEn: 'Other' },
] as const;

type ReasonOption = { key: string; labelEn: string };

/**
 * Modal modes:
 *   - 'reject'         — lister declining a pre-acceptance order ("Decline Order")
 *   - 'lister-cancel'  — lister cancelling a post-acceptance order ("Cancel Order")
 *   - 'eater-cancel'   — eater cancelling their own order ("Cancel Order", with fee preview)
 *
 * Title and confirm-button label vary by reject vs cancel; the reason list
 * varies by lister vs eater so each role sees relevant options.
 */
type Mode = 'reject' | 'lister-cancel' | 'eater-cancel';

interface RejectOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isPending?: boolean;
  mode?: Mode;
  /**
   * Server-computed cancel preview, used only by the eater path. The lister
   * paths leave this undefined and the section is hidden.
   */
  feePreview?: CancelPreviewResponse;
  feePreviewLoading?: boolean;
  /** The order's method, so the lister's time reason names the right window. */
  fulfillmentMethod?: string;
}

export const RejectOrderModal: React.FC<RejectOrderModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isPending = false,
  mode = 'reject',
  feePreview,
  feePreviewLoading = false,
  fulfillmentMethod,
}) => {
  const { t } = useTranslation('orders');
  const isReject = mode === 'reject';
  const isEater = mode === 'eater-cancel';
  const title = isReject ? t('reject.titleReject') : t('reject.titleCancel');
  const intro = isReject ? t('reject.introReject') : t('reject.introCancel');
  const confirmLabel = isReject ? t('reject.confirmReject') : t('reject.confirmCancel');
  const showFeePreview = isEater && (feePreviewLoading || !!feePreview);
  const reasons: readonly ReasonOption[] = isEater
    ? EATER_CANCEL_REASONS
    : fulfillmentMethod === 'dropoff'
      ? LISTER_DROPOFF_REJECTION_REASONS
      : LISTER_REJECTION_REASONS;
  // Used to look up `reject.reasons.<scope>.<key>` for display labels;
  // mirrors `reasons` above (eater vs lister scope).
  const reasonScope: 'eater' | 'lister' = isEater ? 'eater' : 'lister';
  const [selected, setSelected] = useState<string | null>(null);
  const [otherText, setOtherText] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      setOtherText('');
    }
  }, [isOpen]);

  // Resolved reason = wire payload. For canned options we send the EN
  // label so BE analytics stay stable; for "other" we send the user's
  // free-text in whatever language they typed, or fall back to the EN
  // "Other" label so the free-text field stays optional.
  const resolvedReason = (() => {
    if (!selected) return '';
    const option = reasons.find((r) => r.key === selected);
    if (selected === 'other') return otherText.trim() || option?.labelEn || 'Other';
    return option?.labelEn ?? '';
  })();

  const canConfirm = !!selected && !isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <h3 className="text-lg font-bold mb-4 pr-8">{title}</h3>

      <p className="text-sm text-muted-foreground mb-4">{intro}</p>

      {showFeePreview && (
        <div className="mb-4 rounded-lg border border-border bg-muted/30 p-3">
          {feePreviewLoading || !feePreview ? (
            <div className="flex items-center justify-center py-2">
              <IonSpinner name="crescent" className="h-4 w-4" />
            </div>
          ) : feePreview.willCharge ? (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('reject.feeLabel')}</span>
                <span className="font-semibold text-brand-danger">
                  {formatPriceCents(feePreview.fee)}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{t('reject.refundLabel')}</span>
                <span>{formatPriceCents(feePreview.refund)}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-foreground">
              {t('reject.noFeeBody')}
            </p>
          )}
        </div>
      )}

      <RadioOptionList
        className="mb-4"
        ariaLabel={intro}
        options={reasons.map((reason) => ({
          key: reason.key,
          label: t(`reject.reasons.${reasonScope}.${reason.key}`),
        }))}
        value={selected}
        onChange={setSelected}
        disabled={isPending}
      />

      {selected === 'other' && (
        <textarea
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder={t('reject.otherPlaceholder')}
          rows={3}
          className="w-full rounded-lg border border-border bg-background text-foreground px-3 py-2 text-sm focus:border-brand-primary focus:outline-none resize-none mb-4"
        />
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isPending}
          className="flex-1 rounded-lg btn-outline border-border py-2.5 text-sm font-semibold text-foreground active:opacity-80 disabled:opacity-50"
        >
          {t('reject.dismiss')}
        </button>
        <button
          type="button"
          onClick={() => onConfirm(resolvedReason)}
          disabled={!canConfirm}
          className="flex-1 rounded-lg bg-brand-danger py-2.5 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50 flex items-center justify-center"
        >
          {isPending ? (
            <IonSpinner name="crescent" className="h-4 w-4" />
          ) : (
            confirmLabel
          )}
        </button>
      </div>
    </Modal>
  );
};
